import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Box, Button, Chip, Collapse, Fade, IconButton, InputAdornment, Popover, Skeleton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { colors } from "@components/ui/colors";

import { useGetBusinessOwner } from "@components/admins/hooks/useGetBusinessOwner";
import { searchBarPlainStyles } from "@components/jobs/jobs-list/JobsList";
import { ClearIcon } from "@mui/x-date-pickers";
import { ClientNoteCard } from "./ClientNoteCard";
import { ClientNoteForm } from "./ClientNoteForm";
import { useClientNotes } from "./hook/useNotes";
import { INote } from "./interfaces/notes.interface";
import { getFilteredNotes } from "./notesUtils";

export interface ClientNotesProps {
  clientUid?: string | null;
  jobId?: number | null;
  jobEventUid?: string | null;
  notes?: INote[];
  currentUser?: string | null;
}

export const ClientNotes = ({ clientUid = null, jobId = null, jobEventUid = null }: ClientNotesProps) => {
  const {
    notes,
    isLoading: isLoadingNotes,
    createNote,
    updateNote,
    togglePin,
    deleteNote,
    isSaving,
  } = useClientNotes({
    clientUid,
    jobId,
    jobEventUid,
  });

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [tagFilter, setTagFilter] = useState("all");
  const [deleteConfirmNoteId, setDeleteConfirmNoteId] = useState<string | null>(null);
  const newNoteRef = useRef<any>(null);

  const currentUser = useSelector((state: any) => state.userState);
  const currentUserUid = currentUser?.uid || currentUser?.userUid || null;

  const { data: organizationData, isLoading: isLoadingMembers } = useGetBusinessOwner();

  const organizationMembers = useMemo(() => {
    const people = organizationData?.data ?? [];

    return people.reduce((acc: any, person: any) => {
      const user = person?.user;

      const firstName = user?.firstName?.trim() || "";
      const lastName = user?.lastName?.trim() || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const alias = user?.alias?.trim() || "";
      const displayName = user?.displayName?.trim() || "";
      const name = displayName || (user?.useAliasAsDisplayName && alias ? alias : fullName) || user?.email || "Team Member";

      acc[person.userUid] = {
        uid: person.userUid,
        name,
        firstName,
        lastName,
        email: user?.email || "",
        avatar: user?.avatar || null,
        role: person?.userRole?.role?.name || person?.userRole?.role?.type || "",
      };

      return acc;
    }, {});
  }, [organizationData]);

  const counts = useMemo(
    () => ({
      all: notes.length,
      mine: notes.filter((note) => note.userUid === currentUserUid).length,
      pinned: notes.filter((note) => note.pinned).length,
    }),
    [notes, currentUserUid],
  );

  const visibleNotes = useMemo(
    () =>
      getFilteredNotes({
        notes,
        filter,
        search,
        currentUserUid,
        tagFilter,
      }),
    [notes, filter, search, currentUserUid, tagFilter],
  );

  const filters = [
    {
      key: "all",
      label: "All",
      count: counts.all,
    },
    {
      key: "mine",
      label: "My Notes",
      count: counts.mine,
    },
    {
      key: "pinned",
      label: "Pinned",
      count: counts.pinned,
    },
  ];

  const tagFilters = [
    {
      key: "all",
      label: "All tags",
    },
    {
      key: "urgent",
      label: "Urgent",
      color: colors.red?.[500] || "#DC2626",
      bg: "#FEF2F2",
    },
    {
      key: "followup",
      label: "Follow-up",
      color: colors.info?.base || "#2563EB",
      bg: "#EFF6FF",
    },
    {
      key: "billing",
      label: "Billing",
      color: colors.orange[600],
      bg: colors.orange[50],
    },
    {
      key: "general",
      label: "General",
      color: colors.gray[600],
      bg: colors.gray[100],
    },
  ];

  const handleAdd = async (content: string, type: string) => {
    if (!clientUid) return;

    try {
      await createNote({
        content,
        type,
      });

      setShowNoteForm(false);
    } catch (error) {
      console.error("Error adding client note", error);
    }
  };

  const handleSave = async (noteUid: string, content: string, type: string) => {
    if (!clientUid || !noteUid) return;

    try {
      await updateNote({
        noteUid,
        content,
        type,
      });

      setEditingId(null);
    } catch (error) {
      console.error("Error updating client note", error);
    }
  };

  const handleDelete = (noteUid: string) => {
    if (!noteUid) return;
    setDeleteConfirmNoteId(noteUid);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmNoteId) return;

    try {
      await deleteNote(deleteConfirmNoteId);

      if (editingId === deleteConfirmNoteId) {
        setEditingId(null);
      }
    } catch (error) {
      console.error("Error deleting client note", error);
    } finally {
      setDeleteConfirmNoteId(null);
    }
  };

  const handleTogglePin = async (note: INote) => {
    if (!clientUid || !note?.uid) {
      return;
    }

    try {
      await togglePin({
        noteUid: note.uid,
        pinned: !note.pinned,
      });
    } catch (error) {
      console.error("Error updating client note pin", error);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: {
            xs: "wrap",
            md: "nowrap",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            p: 0.4,
            borderRadius: 2.5,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {filters.map((item) => {
            const active = filter === item.key;

            return (
              <Button
                key={item.key}
                size="small"
                onClick={() => setFilter(item.key)}
                sx={{
                  minWidth: "auto",
                  px: 1.25,
                  py: 0.5,
                  gap: 0.75,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 11,
                  fontWeight: 600,
                  color: active ? colors.gray[900] : colors.gray[500],
                  backgroundColor: active ? "var(--fx-surface-base, #FFFFFF)" : "transparent",

                  "&:hover": {
                    backgroundColor: active ? "var(--fx-surface-base, #FFFFFF)" : colors.gray[50],
                  },
                }}
              >
                {item.label}

                <Box
                  component="span"
                  sx={{
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: 10,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: active ? colors.orange[100] : colors.gray[50],
                    color: active ? colors.orange[500] : colors.gray[400],
                  }}
                >
                  {item.count}
                </Box>
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: {
              xs: "100%",
              sm: 220,
            },
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Search notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={searchBarPlainStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: colors.gray[400],
                      fontSize: 18,
                      ml: 0.5,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: !!search && (
                <Fade in>
                  <InputAdornment position="end">
                    <Tooltip title="Clear search" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        sx={{
                          p: 0.25,
                          mr: 0.5,
                        }}
                      >
                        <ClearIcon
                          sx={{
                            fontSize: 16,
                            color: colors.gray[400],
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                </Fade>
              ),
            }}
          />
        </Box>

        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={isSaving || !clientUid}
          onClick={() => {
            if (showNoteForm) {
              newNoteRef.current?.focus();
              return;
            }

            setShowNoteForm(true);

            setTimeout(() => {
              newNoteRef.current?.focus();
            }, 200);
          }}
          sx={{
            flexShrink: 0,
            minWidth: 104,
            height: 36,
            px: 1.75,
            borderRadius: 2,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: colors.orange[500],
            boxShadow: "none",

            "&:hover": {
              backgroundColor: colors.orange[600],
              boxShadow: "none",
            },
          }}
        >
          New Note
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
        }}
      >
        {tagFilters.map((item) => {
          const active = tagFilter === item.key;

          return (
            <Chip
              key={item.key}
              label={item.label}
              size="small"
              onClick={() => setTagFilter(item.key)}
              sx={{
                height: 26,
                borderRadius: 2.5,
                px: 0.25,
                cursor: "pointer",
                fontSize: 10,
                fontWeight: 600,
                color: active ? item.color || colors.orange[500] : colors.gray[500],
                backgroundColor: active ? item.bg || colors.orange[50] : "var(--fx-surface-base, #FFFFFF)",
                border: "1px solid",
                borderColor: active ? item.color || colors.orange.base : colors.gray[100],
                transition: "all 0.15s ease",

                "&:hover": {
                  backgroundColor: item.bg || colors.gray[50],
                  borderColor: item.color || colors.gray[300],
                },
              }}
            />
          );
        })}
      </Box>

      <Collapse in={showNoteForm} timeout={250} unmountOnExit>
        <Fade in={showNoteForm} timeout={300}>
          <Box>
            <ClientNoteForm inputRef={newNoteRef} onAdd={handleAdd} onClose={() => setShowNoteForm(false)} />
          </Box>
        </Fade>
      </Collapse>

      {isLoadingNotes ? (
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Skeleton variant="rounded" width="100%" height={90} sx={{ borderRadius: 2.5 }} />
          <Skeleton variant="rounded" width="100%" height={90} sx={{ borderRadius: 2.5 }} />
        </Stack>
      ) : visibleNotes.length === 0 ? (
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: colors.gray[400],
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              mb: 1,
              borderRadius: 2.5,
              backgroundColor: colors.gray[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StickyNote2OutlinedIcon
              sx={{
                fontSize: 21,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {search ? "No results" : "No notes yet"}
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              fontSize: 11,
            }}
          >
            {search ? "Try searching with different words" : "Add the first note about this client"}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            pt: 1,
          }}
        >
          {visibleNotes.map((note) => {
            const isMine = note.userUid === currentUserUid;

            const author = note.author?.name
              ? {
                ...note.author,
                role: organizationMembers[note.userUid]?.role || "",
              }
              : organizationMembers[note.userUid] || {
                uid: note.userUid,
                name: isLoadingMembers ? "Loading..." : "Team Member",
                email: "",
                avatar: null,
                role: "",
              };

            return (
              <ClientNoteCard
                key={note.uid}
                note={{
                  ...note,
                  author,
                }}
                isMine={isMine}
                isEditing={editingId === note.uid}
                onEdit={() => setEditingId(note.uid)}
                onCancel={() => setEditingId(null)}
                onSave={(content, type) => handleSave(note.uid, content, type)}
                onDelete={() => handleDelete(note.uid)}
                onTogglePin={() => handleTogglePin(note)}
              />
            );
          })}
        </Box>
      )}

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteConfirmNoteId)}
        onClose={() => setDeleteConfirmNoteId(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 260,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.75 }}>Delete note?</Typography>
        <Typography sx={{ fontSize: 11.5, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.45, mb: 2 }}>
          Are you sure you want to delete this record? This action cannot be undone.
        </Typography>
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Button
            size="small"
            onClick={() => setDeleteConfirmNoteId(null)}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.4,
              px: 1.25,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#dc2626",
              color: "white",
              borderRadius: 2,
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
              "&:hover": {
                bgcolor: "#b91c1c",
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};
